import { getLocale } from 'locales/utils';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/fr-ca';

type FormatKey =
  | 'DATE_FORMAT_READABLE'
  | 'DATE_FORMAT_COMPACT'
  | 'DATE_FORMAT_FULL'
  | 'DATE_FORMAT_DATETIME'
  | 'DATE_FORMAT_SPLIT_TIME'
  | 'DATE_FORMAT_DATETIME_SHORT_MONTH';

type SupportedLocale = 'en' | 'en-ca' | 'fr';

function resolveLocale(): SupportedLocale {
  const locale = (getLocale() || '').toLowerCase();

  if (locale.startsWith('en-ca')) {
    return 'en-ca';
  }

  if (locale.includes('fr')) {
    return 'fr';
  }

  return 'en';
}

const FORMAT_MAP: Record<FormatKey, Record<string, string>> = {
  DATE_FORMAT_READABLE: {
    en: 'MMMM DD, YYYY',
    'en-ca': 'DD MMMM, YYYY',
    fr: 'DD MMMM, YYYY',
  },
  DATE_FORMAT_COMPACT: {
    en: 'MMM DD, YYYY',
    'en-ca': 'MMM DD, YYYY',
    fr: 'DD MMM, YYYY',
  },
  DATE_FORMAT_FULL: {
    en: 'MMMM DD, YYYY [at] hh:mm A',
    'en-ca': 'DD MMMM, YYYY [at] hh:mm A',
    fr: 'DD MMMM, YYYY à HH:mm',
  },
  DATE_FORMAT_DATETIME: {
    en: 'MMMM DD, YYYY hh:mm A',
    'en-ca': 'MMM DD, YYYY hh:mm A',
    fr: 'DD MMMM, YYYY HH:mm',
  },
  DATE_FORMAT_DATETIME_SHORT_MONTH: {
    en: 'MMM DD, YYYY, hh:mm A',
    'en-ca': 'MMM DD, YYYY, hh:mm',
    fr: 'DD MMM YYYY, HH [h] mm',
  },
  DATE_FORMAT_SPLIT_TIME: {
    en: 'MMM DD, YYYY|hh:mm A',
    'en-ca': 'MMM DD, YYYY|hh:mm A',
    fr: 'DD MMM, YYYY|HH:mm',
  },
};

// Configuration moved to momentLocaleConfig.ts
// Import and call configureMomentRelativeTime() once during app initialization

export function formatDate(
  date: Date | string | number | null | undefined,
  formatKey: FormatKey,
) {
  if (!date) return '';

  const resolvedLocale = resolveLocale();

  moment.locale(resolvedLocale);

  const formatString = FORMAT_MAP[formatKey][resolvedLocale];
  return moment(date).format(formatString);
}

export function formatDateRelative(
  date: Date | string | number | null | undefined,
  formatKey: FormatKey = 'DATE_FORMAT_FULL',
) {
  if (!date) return '';

  const resolvedLocale = resolveLocale();

  moment.locale(resolvedLocale);

  const mDate = moment(date);
  const formatString = FORMAT_MAP[formatKey][resolvedLocale];

  const absolute = mDate.format(formatString);
  const relative = mDate.fromNow();

  return `${absolute} (${relative})`;
}
